import { PageLayout } from "../../shared/layouts/PageLayout";
import { useParams } from "react-router";
import { adminDashboardBreadcrumbs } from "../../features/AdminDashboard/admin-dashboard-breadcrumbs";
import { useGetUser } from "../../queries/users/useGetUser";
import { useAdminGetUserSubscription } from "@queries/subscription/useAdminGetUserSubscription";
import { ManageSubscription } from "@features/Subscription/ManageSubscription";
import { ISubscriptionInfo } from "@features/Subscription/ManageSubscription/mange-subscription-models";
import styled from "@emotion/styled";
import { Box, Skeleton } from "@mui/material";
import { useGetSubscriptionPlans } from "@queries/subscription-plans/useGetSubscriptionPlans";
import { useAdminCancelUserSubscription } from "@queries/subscription/useAdminCancelUserSubscription";
import { useCallback, useMemo, useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import { SubmitButton } from "@avenue-8/sidekick-ui";
import { useAdminRestoreUserSubscription } from "@queries/subscription/useAdminRestoreUserSubscription";
import { useAdminCancelUserSubscriptionAtPeriodEnd } from "@queries/subscription/useAdminCancelUserSubscriptionAtPeriodEnd";
import { useAdminCancelScheduledSubscriptionCancellation } from "@queries/subscription/useAdminCancelScheduledSubscriptionCancellation";
import { UpdateSubscriptionTrialDaysModal } from "@features/Subscription/ManageSubscription/components/UpdateSubscriptionTrialDaysModal";
import { useAdminUpdateUserSubscriptionTrialDays } from "@queries/subscription/useAdminUpdateUserSubscriptionTrialDays";
import { useAdminRefundUserSubscriptionInvoice } from "@queries/subscription/useAdminRefundUserSubscriptionInvoice";
import { useAdminVoidUserSubscriptionInvoice } from "@queries/subscription/useAdminVoidUserSubscriptionInvoice";
import { useAdminTurnUserSubscriptionToFree } from "@queries/subscription/useAdminTurnUserSubscriptionToFree";

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 2rem;
  border-radius: 1rem;
  color: ${(p) => p.theme.text.colors.primary};
  background-color: ${(p) => p.theme.colors.background};
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.2);
  width: 100%;

  &[aria-disabled="true"] {
    opacity: 0.5;
    pointer-events: none;
  }
`;

export function ManageUserSubscriptionPage() {
  const [forceResetUserCache, setForceResetUserCache] = useState(false);
  const [restoreSubscriptionModalOpen, setRestoreSubscriptionModalOpen] =
    useState(false);
  const [
    updateSubscriptionTrialDaysModalOpen,
    setUpdateSubscriptionTrialDaysModalOpen,
  ] = useState(false);
  const { userId = "" } = useParams<{
    userId: string;
  }>();
  const userQuery = useGetUser({
    id: userId,
  });
  const user = userQuery.data;
  const { data: userSubscription, isLoading: isLoadingUserSubscription } =
    useAdminGetUserSubscription({
      userId,
      forceRefresh: forceResetUserCache,
    });

  const refreshUserCache = () => {
    setForceResetUserCache(true);
  };

  const breadcrumbs = {
    current: {
      label: "Subscription",
    },
    previous: [
      adminDashboardBreadcrumbs.dashboard,
      adminDashboardBreadcrumbs.users,
      {
        label: user?.name || user?.email || "",
        link: `/admin/users/${userId}`,
      },
    ],
  };

  const cancelUserSubscriptionMutation = useAdminCancelUserSubscription();
  const restoreUserSubscriptionMutation = useAdminRestoreUserSubscription({
    onSuccess: () => {
      setRestoreSubscriptionModalOpen(false);
    },
  });
  const updateUserSubscriptionTrialDaysMutation =
    useAdminUpdateUserSubscriptionTrialDays({
      onSuccess: () => {
        setUpdateSubscriptionTrialDaysModalOpen(false);
      },
    });
  const cancelUserSubscriptionAtPeriodEndMutation =
    useAdminCancelUserSubscriptionAtPeriodEnd();
  const cancelScheduledSubscriptionCancellationMutation =
    useAdminCancelScheduledSubscriptionCancellation();
  const turnToFreeMutation = useAdminTurnUserSubscriptionToFree();

  const handleCancelSubscription = useCallback(async () => {
    return cancelUserSubscriptionMutation.mutateAsync({ userId });
  }, [cancelUserSubscriptionMutation, userId]);

  const handleCancelSubscriptionAtPeriodEnd = useCallback(async () => {
    return cancelUserSubscriptionAtPeriodEndMutation.mutateAsync({ userId });
  }, [cancelUserSubscriptionAtPeriodEndMutation, userId]);

  const handleRestoreSubscription = useCallback(
    (params: { trialDays: number }) => {
      const { trialDays } = params;
      restoreUserSubscriptionMutation.mutate({ userId, trialDays });
    },
    [restoreUserSubscriptionMutation, userId],
  );

  const handleRestoreSubscriptionClick = () =>
    setRestoreSubscriptionModalOpen(true);

  const handleUpdateSubscriptionTrialDays = useCallback(
    (params: { trialDays?: number | null; trialEndDate?: Date | null }) => {
      const { trialDays, trialEndDate } = params;
      updateUserSubscriptionTrialDaysMutation.mutate({
        userId,
        trialDays: trialDays ? trialDays : null,
        trialEndDate: trialEndDate ? trialEndDate : null,
      });
    },
    [updateUserSubscriptionTrialDaysMutation, userId],
  );

  const handleUpdateSubscriptionTrialDaysClick = () =>
    setUpdateSubscriptionTrialDaysModalOpen(true);

  const handleCancelScheduledSubscriptionCancellation =
    useCallback(async () => {
      return cancelScheduledSubscriptionCancellationMutation.mutateAsync({
        userId,
      });
    }, [cancelScheduledSubscriptionCancellationMutation, userId]);

  const handleTurnToFree = useCallback(async () => {
    return turnToFreeMutation.mutateAsync({ userId });
  }, [turnToFreeMutation, userId]);

  const subscriptionPlansQuery = useGetSubscriptionPlans();

  const subscriptionInfo = useMemo((): ISubscriptionInfo | null => {
    const data = userSubscription;
    if (!data) return null;

    if (data.error) {
      return {
        type: "invalid" as const,
        name: "Sidekick Subscription",
        errorDescription:
          "There is an issue with this subscription, and it is currently not possible to make any change. If the problem persists, please contact support.",
        paymentMethod: null,
        billingDetails: null,
      };
    }
    if (data.type === "free") {
      return {
        type: "free" as const,
        name: "Sidekick Free Subscription",
        paymentMethod: null,
        billingDetails: null,
      };
    }
    if (data.type === "paid" && data.details) {
      const { details } = data;
      return {
        type: "paid" as const,
        name: "Sidekick Subscription",
        price: details.nextPaymentAmount,
        trialEndDate: details.trialEndDate ? details.trialEndDate : null,
        cancellationDate: details.cancellationDate ?? null,
        scheduledCancellationDate: details.scheduledCancellationDate ?? null,
        nextPaymentDate: details.nextPaymentDate ?? null,
        lastPaymentDate: details.lastPaymentDate ?? null,
        paymentStatus: details.paymentStatus,
        paymentMethodStatus: details.paymentMethodStatus,
        credits: details.credits ?? 0,
        estimatedPaymentMethodRequiredDate:
          details.estimatedPaymentMethodRequiredDate ?? null,
        paymentMethod: details.paymentMethod
          ? {
              type: details.paymentMethod.type,
              brand: details.paymentMethod.brand,
              last4: details.paymentMethod.last4,
              expMonth: details.paymentMethod.expiryMonth,
              expYear: details.paymentMethod.expiryYear,
            }
          : null,
        billingDetails: details.billingDetails?.state
          ? {
              address: {
                state: details.billingDetails.state ?? "",
                city: details.billingDetails.city ?? "",
                line1: details.billingDetails.line1 ?? "",
                line2: details.billingDetails.line2 ?? "",
                zipCode: details.billingDetails.zipCode ?? "",
                country: details.billingDetails.country ?? "US",
              },
            }
          : null,
        invoices: details.invoices ?? [],
      };
    }

    return {
      type: "invalid" as const,
      name: "Sidekick Subscription",
      errorDescription:
        "There is an issue with your subscription, and it is currently not possible to make any change. If the problem persists, please contact support.",
      paymentMethod: null,
      billingDetails: null,
    };
  }, [userSubscription]);

  const userInfo = useMemo(() => {
    if (!user || !userSubscription) return null;
    return {
      id: user.id,
      name: user?.name ?? "",
      email: user?.email ?? "",
      subscriptionPlan: {
        trialEndDate: userSubscription.details?.trialEndDate ?? null,
        name:
          subscriptionPlansQuery.data?.items.find(
            (item) => item.id === user?.subscriptionPlanId,
          )?.name ?? "Unknown",
      },
    };
  }, [user, userSubscription, subscriptionPlansQuery.data]);

  const refundInvoiceMutation = useAdminRefundUserSubscriptionInvoice();
  const voidInvoiceMutation = useAdminVoidUserSubscriptionInvoice();

  const handleVoidInvoice = useCallback(
    (invoiceId: string) => {
      voidInvoiceMutation.mutate({ userId, invoiceId });
    },
    [voidInvoiceMutation, userId],
  );

  const handleRefundInvoice = useCallback(
    (invoiceId: string) => {
      refundInvoiceMutation.mutate({ userId, invoiceId });
    },
    [refundInvoiceMutation, userId],
  );

  const isMutating = useIsMutating({ mutationKey: ["admin-subscription"] }) > 0;

  return (
    <PageLayout breadcrumbs={breadcrumbs}>
      <Container>
        {isLoadingUserSubscription && (
          <Skeleton variant="rounded" height={600} width={"100%"} />
        )}
        <Box
          mb={2}
          sx={{
            display: "flex",
            position: "absolute",
            top: "-3.5rem",
            right: "0rem",
          }}
        >
          <SubmitButton
            onClick={refreshUserCache}
            size="small"
            variant="outlined"
            startIcon={
              isLoadingUserSubscription ? undefined : forceResetUserCache ? (
                <CheckIcon />
              ) : (
                <RefreshIcon />
              )
            }
            disabled={forceResetUserCache}
            type="button"
            isLoading={isLoadingUserSubscription}
            loadingText="Refreshing..."
          >
            {forceResetUserCache ? "Cache refreshed" : "Refresh Cache"}
          </SubmitButton>
        </Box>
        {!!userSubscription &&
          !!subscriptionInfo &&
          !!userInfo &&
          !isLoadingUserSubscription && (
            <ManageSubscription
              role="admin"
              subscriptionInfo={subscriptionInfo}
              readOnlyConfig={{
                paymentMethod: true,
              }}
              userInfo={userInfo}
              disabled={isMutating}
              onCloseRestoreSubscriptionModal={() =>
                setRestoreSubscriptionModalOpen(false)
              }
              isRestoreSubscriptionModalOpen={restoreSubscriptionModalOpen}
              handleCancelSubscription={
                userSubscription.actions?.cancel
                  ? handleCancelSubscription
                  : undefined
              }
              handleCancelSubscriptionAtPeriodEnd={
                userSubscription.actions?.cancelAtEndOfPeriod
                  ? handleCancelSubscriptionAtPeriodEnd
                  : undefined
              }
              handleRestoreSubscription={handleRestoreSubscription}
              onRestoreSubscriptionClick={
                userSubscription.actions?.restore
                  ? handleRestoreSubscriptionClick
                  : undefined
              }
              handleCancelScheduledSubscriptionCancellation={
                userSubscription.actions?.cancelScheduledCancellation
                  ? handleCancelScheduledSubscriptionCancellation
                  : undefined
              }
              onUpdateTrialPeriodClick={
                userSubscription.actions?.updateTrialPeriod
                  ? handleUpdateSubscriptionTrialDaysClick
                  : undefined
              }
              handleTurnToFree={
                userSubscription.actions?.turnToFree
                  ? handleTurnToFree
                  : undefined
              }
              handleVoidInvoice={handleVoidInvoice}
              handleRefundInvoice={handleRefundInvoice}
            />
          )}
      </Container>

      {!!userInfo && (
        <UpdateSubscriptionTrialDaysModal
          open={updateSubscriptionTrialDaysModalOpen}
          isLoading={updateUserSubscriptionTrialDaysMutation.isPending}
          onClose={() => setUpdateSubscriptionTrialDaysModalOpen(false)}
          userInfo={userInfo}
          onUpdateSubscriptionTrialDays={handleUpdateSubscriptionTrialDays}
        />
      )}
    </PageLayout>
  );
}
